import { FCC } from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router";
import { userTenantService } from "../../../../shared/api/userTenantService";
import { TenantOptionResource } from "../../../../shared/types/tenant";
import { tenantService } from "../../shared/api/tenantService";
import { SelectedTenantResponse } from "../../shared/types/tenant";

interface TenantProviderState {
	selectTenant: (tenant: TenantOptionResource | null) => Promise<void>;
	tenants: TenantOptionResource[];
	selected: SelectedTenantResponse;
}

const GlobalTenantProviderContext = createContext<TenantProviderState | undefined>(undefined);

export const GlobalTenantProvider: FCC = ({ children }) => {
	const queryClient = useQueryClient();
	const { pathname } = useLocation();
	const navigate = useNavigate();

	const { data: tenants } = useQuery({
		queryKey: userTenantService.allOptionsKey,
		queryFn: () => userTenantService.allOptions().then((r) => r.data.data),
	});

	const { data: selected } = useQuery({
		queryKey: tenantService.selectedKey,
		queryFn: () => tenantService.selected().then((r) => r.data.data),
	});

	const selectTenant = useCallback(
		async (tenant: TenantOptionResource | null) => {
			if (tenant) {
				await tenantService.select(tenant.id);
				await queryClient.resetQueries();
				["contacts", "companies", "clippings", "distribution-lists"].forEach((path) => {
					if (pathname.includes(path) && !pathname.includes("events")) {
						navigate(`/${path}`);
					}
				});
			}
		},
		[queryClient, pathname, navigate],
	);

	if (tenants === undefined || selected === undefined) {
		return null;
	}

	return (
		<GlobalTenantProviderContext.Provider
			value={{
				selectTenant,
				tenants,
				selected,
			}}
		>
			{children}
		</GlobalTenantProviderContext.Provider>
	);
};

export const useGlobalTenants = () => {
	const ctx = useContext(GlobalTenantProviderContext);

	if (ctx === undefined) {
		throw new Error(
			"GlobalTenantProviderContext undefined. Did you forget to wrap your components in GlobalTenantProvider?",
		);
	}

	return ctx;
};
